"use client";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import { getStudentsByStatus } from "../../../services/studentServices";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import { Pagination } from "../../../components/admin/common/PaginationNumber";
import StudentDetailsModal from "../../../components/admin/students/StudentDetailsModal";

const ExpiredStudentList = () => {
  const [page, setPage] = useState(1);
  const [entriesOption, setEntriesOption] = useState({
    value: 10,
    label: "10",
  });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const limit = entriesOption.value;

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["students", "expired", page, limit],
    queryFn: () =>
      getStudentsByStatus({
        status: "expired",
        page,
        limit,
      }),
    keepPreviousData: true,
    staleTime: 30000,
  });

  const students = data?.data?.students || [];
  const totalPages = data?.data?.totalPages || 1;
  const total = data?.data?.total || 0;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setPage(1);
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedStudent(null), 300);
  };

  const startIndex = (page - 1) * limit;

  return (
    <div className="flex h-screen p-4 gap-4 bg-white">
      <div className="flex-shrink-0">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                <Header
                  title="Expired Student List"
                  desc="Review, approve, or reject student registered applications"
                />

                {/* Entries Dropdown */}
                <div className="flex items-center gap-2 mb-4 mt-6">
                  <div className="w-20">
                    <DropdownSelect
                      selected={entriesOption}
                      onChange={handleEntriesChange}
                      options={[
                        { value: 10, label: "10" },
                        { value: 25, label: "25" },
                        { value: 50, label: "50" },
                        { value: 100, label: "100" },
                      ]}
                    />
                  </div>
                </div>

                {isLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                    <p className="ml-4 text-gray-600 text-sm">
                      Loading pending students...
                    </p>
                  </div>
                )}

                {isError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    <p className="font-semibold text-sm">
                      Error loading students
                    </p>
                    <p className="text-xs">
                      {error?.message || "Failed to fetch pending students"}
                    </p>
                  </div>
                )}

                {!isLoading && !isError && (
                  <>
                    {isFetching && !isLoading && (
                      <div className="mb-2 px-4 py-2 bg-blue-50 text-blue-700 rounded text-xs">
                        Updating...
                      </div>
                    )}

                    <div className="bg-white rounded-lg shadow">
                      <div className="overflow-x-hidden rounded-t-xl">
                        <table className="w-full text-xs border border-gray-300">
                          <thead className="bg-[#4F22D8] text-white text-sm">
                            <tr>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-12">
                                No
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[180px]">
                                Full Name
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[160px]">
                                Email
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[110px]">
                                Mobile
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[140px]">
                                Institution
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[80px]">
                                Degree
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[100px]">
                                Applied Date
                              </th>
                              <th className="py-3 px-3 text-left font-bold w-[180px]">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {students.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="text-center text-gray-500 py-6 border-t border-gray-300 text-sm"
                                >
                                  No expired students found.
                                </td>
                              </tr>
                            ) : (
                              students.map((student, idx) => (
                                <tr
                                  key={student._id}
                                  className="border-t border-gray-300 text-xs hover:bg-gray-50 transition-colors"
                                >
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300">
                                    {startIndex + idx + 1}
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300">
                                    <div className="flex items-center gap-2">
                                      {student.photo && (
                                        <img
                                          src={student.photo}
                                          alt={student.fullName}
                                          className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                                        />
                                      )}
                                      <span className="font-semibold break-words">
                                        {student.fullName}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300 break-all">
                                    {student.email}
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300">
                                    {student.mobile}
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300 break-words">
                                    {student.institutionDetails?.[0]
                                      ?.institutionName || "N/A"}
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300 break-words">
                                    {student.institutionDetails?.[0]?.degree ||
                                      "N/A"}
                                  </td>
                                  <td className="px-3 py-3 text-black border-r border-gray-300 whitespace-nowrap">
                                    {new Date(
                                      student.createdAt
                                    ).toLocaleDateString("en-IN")}
                                  </td>
                                  <td className="px-3 py-3">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <button
                                        onClick={() =>
                                          handleViewStudent(student)
                                        }
                                        className="px-2.5 py-1.5 bg-[#4f22d8] text-white rounded  transition-colors text-[11px] font-medium whitespace-nowrap"
                                      >
                                        View
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Pagination */}
                    {students.length > 0 && (
                      <div className="mt-4">
                        <Pagination
                          totalPages={totalPages}
                          currentPage={page}
                          onPageChange={handlePageChange}
                          showPages={5}
                        />
                      </div>
                    )}

                    {/* Showing results text */}
                    {students.length > 0 && (
                      <div className="text-center text-xs text-gray-600 mt-2">
                        Showing {startIndex + 1} to{" "}
                        {Math.min(startIndex + students.length, total)} of{" "}
                        {total} entries
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </PageWrapper>
      </div>

      <StudentDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        student={selectedStudent}
      />
    </div>
  );
};

export default ExpiredStudentList;
